import { renderTicketHtmlDocument } from '@/services/printing/ticketDocumentService';
import { formatDateTime, normalizeNumber } from '@/services/printing/ticketReceiptPrinter';
import { resolveTemplateKeyForPrintEvent } from '@/services/printing/printJobService';

function normalizeItems(payload = {}) {
  if (Array.isArray(payload.items) && payload.items.length) {
    return payload.items;
  }
  if (Array.isArray(payload.shopping_carts) && payload.shopping_carts.length) {
    return payload.shopping_carts;
  }
  if (Array.isArray(payload.block?.items) && payload.block.items.length) {
    return payload.block.items;
  }
  return [];
}

function selectedStepItems(step = {}) {
  if (Array.isArray(step.plates_selected)) return step.plates_selected;
  if (Array.isArray(step.plates)) return step.plates;
  if (step.plate_selected) return [step.plate_selected];
  return [];
}

function buildItemDetails(item = {}) {
  const steps = Array.isArray(item.steps_selected)
    ? item.steps_selected
    : Array.isArray(item.steps)
      ? item.steps
      : [];

  return steps
    .map((step, index) => {
      const selectedNames = selectedStepItems(step)
        .map((selected) => (
          selected?.plate?.name
          || selected?.custom_name
          || selected?.name
          || selected?.plate_name
          || ''
        ))
        .filter(Boolean);

      if (!selectedNames.length) return null;
      return `${step?.name || step?.title || `Paso ${index + 1}`}: ${selectedNames.join(', ')}`;
    })
    .filter(Boolean);
}

function moneyLabel(value) {
  return `${normalizeNumber(value).toFixed(2)} EUR`;
}

export function buildOperationalPrintTemplatePayload(templateKey, payload = {}, options = {}) {
  const sourceItems = normalizeItems(payload);
  const showPrices = options.showPrices !== false;
  const items = sourceItems.map((item) => {
    const quantity = normalizeNumber(item.amount ?? item.quantity ?? 0);
    const unitPrice = normalizeNumber(item.unit_price ?? item.price ?? item.plate?.price ?? 0);
    const totalAmount = normalizeNumber(
      item.total_amount
      ?? item.total
      ?? quantity * unitPrice
    );

    return {
      name: item.plate_name
        || item.product_name_snapshot
        || item.name
        || item.plate?.name
        || 'Producto',
      quantity,
      unit_price_label: showPrices ? moneyLabel(unitPrice) : '',
      total_amount_label: showPrices ? moneyLabel(totalAmount) : '',
      comment: item.comment || item.notes || '',
      details: buildItemDetails(item),
      category_id: String(
        item.category_id
        || item.product_category_id
        || item.category?.id
        || item.plate?.category_id
        || item.plate?.category?.id
        || ''
      ) || null,
    };
  });

  const grandTotal = normalizeNumber(
    payload.total
    ?? payload.subtotal
    ?? payload.total_amount
    ?? payload.block?.subtotal
    ?? items.reduce((sum, item, index) => {
      const sourceItem = sourceItems[index] || {};
      const quantity = normalizeNumber(sourceItem.amount ?? sourceItem.quantity ?? 0);
      const unitPrice = normalizeNumber(sourceItem.unit_price ?? sourceItem.price ?? sourceItem.plate?.price ?? 0);
      return sum + normalizeNumber(sourceItem.total_amount ?? sourceItem.total ?? quantity * unitPrice);
    }, 0)
  );

  return {
    template_key: templateKey,
    channel: payload.channel || 'shared',
    template_format: 'html',
    restaurant: {
      name: payload.restaurantName || payload.restaurant_name || 'Eat with Us TPV',
    },
    document: {
      title: payload.title || options.defaultTitle || 'Impresion',
      created_at: formatDateTime(
        payload.createdAt
        || payload.created_at
        || payload.created_at_local
        || payload.block?.created_at
        || Date.now()
      ),
      table_label: payload.tableLabel
        || payload.table_label
        || payload.block?.tableLabel
        || payload.block?.table_label
        || '-',
      // Planta para la cabecera «Planta - Mesa» de la comanda ampliada (agente 2.6.0).
      floor_label: payload.floorLabel || payload.floor_label || payload.block?.floor_label || '',
      customer_name: payload.customerName || payload.customer_name || '',
      origin_label: payload.originLabel || payload.origin_label || options.originLabel || '',
    },
    items,
    totals: {
      summary_rows: Array.isArray(payload.summary_rows) ? payload.summary_rows : [],
      total_label: showPrices ? (payload.totalLabel || 'TOTAL') : '',
      grand_total_label: showPrices ? moneyLabel(grandTotal) : '',
    },
    footer: {
      message: payload.footerText || options.footerMessage || '',
    },
    metadata: {
      ...(payload.metadata || {}),
      template_key: templateKey,
      order_id: payload.order_id || payload.block?.order_id || null,
      local_order_id: payload.local_order_id || null,
    },
  };
}

export async function renderOperationalPrintTemplate(templateKey, payload = {}, options = {}) {
  const templatePayload = buildOperationalPrintTemplatePayload(templateKey, payload, options);
  const rendered = await renderTicketHtmlDocument(templatePayload, {
    title: templatePayload.document.title,
    timeoutMs: options.timeoutMs,
    strictOnline: options.strictOnline,
  });

  return {
    ...rendered,
    payload: templatePayload,
  };
}

export async function renderOperationalPrintEvent(eventKey, payload = {}, options = {}) {
  const templateKey = resolveTemplateKeyForPrintEvent(eventKey);
  if (!templateKey) {
    throw new Error(`No existe un diseno asociado al evento de impresion '${eventKey}'.`);
  }
  return renderOperationalPrintTemplate(templateKey, payload, options);
}
